<template>
  <div class="custom-fields-tab pa-4">
    <v-skeleton-loader
      v-if="isLoading"
      type="list-item, list-item, list-item"
    />

    <template v-else>
      <div
        v-if="!fields.length"
        class="text-center secondary--text py-8"
      >
        カスタムフィールドがありません
      </div>

      <template v-else>
        <v-alert
          dense
          text
          type="info"
          class="text-caption mb-3"
        >
          保存した変更を集計値へ反映するには再集計を実行してください
        </v-alert>

        <div
          v-for="field in fields"
          :key="`custom-field-${field.id}`"
          class="custom-field-row"
        >
          <template v-if="field.fieldType === 'boolean'">
            <div class="d-flex align-center justify-space-between">
              <span class="custom-field-row__label">{{ field.name }}</span>
              <v-switch
                v-model="formValues[field.id]"
                dense
                hide-details
                class="mt-0 pt-0"
              />
            </div>
          </template>

          <template v-else>
            <v-text-field
              v-model="formValues[field.id]"
              :label="field.name"
              :type="field.fieldType === 'number' ? 'number' : 'text'"
              dense
            />
          </template>
        </div>

        <v-btn
          block
          color="primary"
          class="mt-4"
          :loading="isSaving"
          :disabled="!hasChanges || isSaving"
          @click="save"
        >
          保存
        </v-btn>
      </template>
    </template>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import UserCustomFieldValueApi from '@/api/v2/eod-closing/UserCustomFieldValue'
import { handleApiError } from '@/utils/errorHandler'

export default {
  name: 'UserCustomFieldsTab',
  props: {
    userId: {
      type: Number,
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const isLoading = ref(false)
    const isSaving = ref(false)
    const fields = ref([])

    // storeCustomFieldId => 入力値（boolean は Boolean、それ以外は String）
    const formValues = ref({})
    const originalValues = ref({})

    // API の value(string) を入力用の値へ変換する
    const toFormValue = field => {
      if (field.fieldType === 'boolean') return field.value === 'true'

      return field.value ?? ''
    }

    // 入力値を API へ送る string へ変換する
    const toApiValue = (field, formValue) => {
      if (field.fieldType === 'boolean') return formValue ? 'true' : 'false'

      return String(formValue)
    }

    const buildForm = () => {
      const values = {}
      fields.value.forEach(field => {
        values[field.id] = toFormValue(field)
      })
      formValues.value = values
      originalValues.value = { ...values }
    }

    const fetchFields = async () => {
      if (!props.userId) return

      isLoading.value = true
      try {
        const res = await UserCustomFieldValueApi.getUserCustomFieldValues({ userId: props.userId })
        if (res?.data?.status === 'success') {
          fields.value = res.data.data.fields || []
          buildForm()
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      } finally {
        isLoading.value = false
      }
    }

    const changedFields = computed(() => {
      return fields.value.filter(field => formValues.value[field.id] !== originalValues.value[field.id])
    })

    const hasChanges = computed(() => changedFields.value.length > 0)

    const save = async () => {
      // 空欄のフィールドは送信しない（number の空文字はバリデーションエラーになるため。値クリアはスコープ外）
      const values = changedFields.value
        .filter(field => field.fieldType === 'boolean' || String(formValues.value[field.id]).trim() !== '')
        .map(field => ({
          storeCustomFieldId: field.id,
          value: toApiValue(field, formValues.value[field.id]),
        }))

      if (!values.length) return

      isSaving.value = true
      try {
        const res = await UserCustomFieldValueApi.bulkUpdateUserCustomFieldValues({
          userId: props.userId,
          values,
        })
        if (res?.data?.status === 'success') {
          vm.$toast.success('カスタムフィールドを保存しました')
          await fetchFields()
        } else if (res?.data?.status === 'error') {
          const { message } = res.data
          const messages = Array.isArray(message) ? message : [message]
          messages.forEach(msg => vm.$toast.error(msg))
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      } finally {
        isSaving.value = false
      }
    }

    watch(() => props.userId, () => {
      fields.value = []
      formValues.value = {}
      originalValues.value = {}
      fetchFields()
    }, { immediate: true })

    return {
      isLoading,
      isSaving,
      fields,
      formValues,
      hasChanges,
      save,
    }
  },
}
</script>

<style lang="scss" scoped>
.custom-fields-tab {
  overflow-y: auto;
}

.custom-field-row {
  padding: 4px 0;

  &__label {
    font-size: 13px;
  }
}
</style>
